import { useState, useEffect } from "react"; // v2
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Check, ArrowRight, Shield, Zap, Ship, CalendarCheck, CreditCard, Users, BarChart3, Bell, Clock, RefreshCw, ExternalLink, Apple, Smartphone, CheckCircle } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { HealthScoreCard } from "./components/HealthScoreCard";
import { SkeletonAccountCard, SkeletonHealthCard, SkeletonList } from "./components/visual/Skeleton";
import { ProviderBadge } from "./components/ProviderBadge";
import { fmtEur, fmtDate } from "./lib/format";
import { usePageMeta } from "./lib/usePageMeta";
import { usePageT } from "./i18n/LanguageContext";
import { LanguageSwitcher } from "./components/LanguageSwitcher";

const API = process.env.REACT_APP_API_URL || ""; // Single backend (Railway)

// ==================== TRANSLATIONS ====================
const translations = {
  it: {
    // PricingPage meta
    metaPricingTitle: "Prezzi · Nautiqo",
    metaPricingDesc: "Un piano. Tutto incluso. €19,90 al mese o €199 all'anno. Cancella quando vuoi.",
    // Nav
    navDashboard: "Dashboard",
    navLogin: "Accedi",
    // Checkout error
    checkoutError: "Errore durante il checkout. Riprova tra qualche secondo.",
    // Features
    featUnlimitedBoats: "Barche illimitate",
    featCalendar: "Calendario e disponibilita",
    featCustomers: "Gestione clienti e prenotazioni",
    featPayments: "Pagamenti Stripe dal tuo sito",
    featNotifications: "Notifiche push + email",
    featReports: "Report e statistiche",
    featAutomations: "Automazioni avanzate",
    featStaff: "Staff multi-utente",
    featIcal: "Sincronizzazione iCal",
    // Hero
    subscriptionLabel: "Abbonamento",
    heroTitle: "Un piano. Tutto incluso.",
    heroSubtitle: "Gestisci il tuo charter senza limiti. Scegli la frequenza di fatturazione che preferisci.",
    // Billing toggle
    monthly: "Mensile",
    annual: "Annuale",
    // Checkout banners
    subActivatedTitle: "Abbonamento attivato!",
    subActivatedDesc: "Il tuo account è ora attivo. Vai alla dashboard per iniziare.",
    checkoutCancelled: "Checkout annullato. Puoi riprovare quando vuoi.",
    // Pricing card
    billingAnnual: "Fatturazione annuale",
    billingMonthly: "Fatturazione mensile",
    perMonth: "/mese",
    perYear: "/anno",
    savePrefix: "Risparmi",
    savePerYearSuffix: "all'anno",
    cancelAnytime: "Cancella quando vuoi. Nessun vincolo.",
    redirectingStripe: "Reindirizzamento a Stripe...",
    subscribeWithCard: "Abbonati con carta",
    registerFirst: " — Registrati prima",
    orFromApp: "oppure dall'app",
    goToDashboard: "Vai alla dashboard →",
    alreadyHaveAccount: "Hai già un account?",
    securePayment: "Pagamento sicuro",
    cardsAccepted: "Visa, Mastercard, SEPA",
    cancelOneClick: "Cancella in 1 click",
    mobilePricingDisclaimer: "Per acquisti tramite App Store (iPhone) o Google Play (Android), il prezzo può variare in base al tuo paese e sarà mostrato dallo store al momento dell'acquisto.",
    // AccountPage meta
    metaAccountTitle: "Account · Nautiqo",
    metaAccountDesc: "Gestisci il tuo abbonamento Nautiqo.",
    // Toasts
    toastSubActivatedTitle: "Abbonamento attivato!",
    toastSubActivatedDesc: "Il tuo account è ora attivo. Benvenuto in Nautiqo.",
    toastCheckoutCancelledTitle: "Checkout annullato",
    toastCheckoutCancelledDesc: "Puoi riprovare in qualsiasi momento.",
    toastAutoRenewCancelledTitle: "Rinnovo automatico annullato",
    toastAutoRenewCancelledDesc1: "Il tuo account resterà attivo fino al",
    toastCancelErrorTitle: "Errore durante l'annullamento",
    toastTryLater: "Riprova più tardi.",
    toastReactivateAppTitle: "Riattiva dalla tua app Nautiqo",
    toastReactivateAppDesc: "Apri l'app sullo smartphone e rinnova l'abbonamento da App Store (iOS) o Google Play (Android).",
    toastChangePlanAppTitle: "Cambia piano dalla tua app Nautiqo",
    toastChangePlanAppDesc: "Mensile/annuale possono essere modificati da App Store (iOS) o Google Play (Android).",
    toastPlanUpdatedTitle: "Piano aggiornato",
    toastSwitchedAnnual: "Sei passato al piano annuale.",
    toastSwitchedMonthly: "Sei passato al piano mensile.",
    toastChangePlanErrorTitle: "Errore nel cambio piano",
    toastPortalErrorTitle: "Impossibile aprire il portale",
    // Status labels
    statusTrialActive: "Trial attivo",
    statusActive: "Attivo",
    statusRenewCancelled: "Rinnovo annullato",
    statusPastDue: "Pagamento in sospeso",
    statusExpired: "Scaduto",
    statusTrialExpired: "Trial scaduto",
    statusInactive: "Inattivo",
    // Account page body
    accountTitle: "Account",
    accountSubtitle: "Gestisci il tuo abbonamento Nautiqo",
    subscription: "Abbonamento",
    plan: "Piano",
    trialExpiresOn: "Trial scade il",
    accessValidUntil: "Accesso valido fino al",
    nextRenewal: "Prossimo rinnovo",
    expiredOn: "Scaduto il",
    daysSuffix: "giorni",
    channel: "Canale",
    email: "Email",
    cancelPendingPart1: "Il rinnovo automatico è stato annullato il",
    cancelPendingPart2: ". Il tuo account resterà attivo fino al",
    cancelPendingPart3: ". Puoi riattivare il rinnovo in qualsiasi momento prima della scadenza.",
    trialUsingPart1: "Stai usando Nautiqo in modalità",
    trialFreeTrial: "prova gratuita",
    trialUsingPart2: ". Hai ancora",
    trialUsingPart3: "per attivare il piano e gestire il rinnovo automatico dal sito.",
    // Subscription management
    manageSubscription: "Gestione abbonamento",
    manageDescApple: "Il tuo abbonamento è stato attivato tramite App Store e può essere gestito solo dal tuo iPhone/iPad.",
    manageDescGoogle: "Il tuo abbonamento è stato attivato tramite Google Play e può essere gestito solo dal tuo dispositivo Android.",
    manageDescStripeActive: "Annulla il rinnovo automatico — l'accesso resta attivo fino alla scadenza.",
    manageDescStripeCancelPending: "Riattiva il rinnovo automatico per continuare senza interruzioni.",
    manageDescExpired: "Il tuo abbonamento è scaduto. Riattivalo per tornare operativo.",
    manageDescSetup: "Configura il rinnovo automatico per non perdere mai l'accesso al servizio.",
    manageDescLegacy: "Hai un abbonamento attivo dal flusso precedente.",
    // Apple block
    manageOnAppStore: "Gestisci su App Store",
    toModifyOrCancel: "Per modificare o annullare questo abbonamento:",
    appleStep1Pre: "Apri",
    appleStep1Bold: "Impostazioni",
    appleStep1Post: "sul tuo iPhone o iPad",
    appleStep2Pre: "Tocca",
    appleStep2Bold: "il tuo nome",
    appleStep2Post: "in cima",
    appleStep3Pre: "Vai su",
    appleStep3Bold: "Abbonamenti",
    stepSelectPre: "Seleziona",
    stepSelectPost: "e scegli l'azione desiderata",
    openAppStoreSubs: "Apri Abbonamenti App Store",
    // Google block
    manageOnGooglePlay: "Gestisci su Google Play",
    googleStep1Pre: "Apri l'app",
    googleStep1Bold: "Play Store",
    googleStep1Post: "su Android",
    googleStep2Pre: "Tocca",
    googleStep2Bold: "il tuo profilo",
    googleStep2Post: "in alto a destra",
    googleStep3Pre: "Vai su",
    googleStep3Bold: "Pagamenti e abbonamenti → Abbonamenti",
    openGooglePlaySubs: "Apri Abbonamenti Google Play",
    // Stripe actions
    switchTo: "Cambia a",
    updatePaymentInvoices: "Aggiorna pagamento e fatture",
    cancelAutoRenew: "Annulla rinnovo automatico",
    accountActiveUntilPart1: "Il tuo account resta attivo fino al",
    accountActiveUntilPart2: ". Nessun addebito al rinnovo.",
    reactivateAutoRenew: "Riattiva rinnovo automatico",
    willBeChargedPart1: "Verrà addebitato",
    willBeChargedPart2Day: "il",
    reactivateSubscription: "Riattiva abbonamento",
    setupAutoRenew: "Configura rinnovo automatico",
    chooseMonthlyAnnual: "Scegli Mensile o Annuale. Cancella quando vuoi, nessun vincolo.",
    contactSupport: "Contatta l'assistenza",
    switchToNewPlan: "Passa al nuovo piano con rinnovo automatico",
    legacyInfo: "Il tuo piano attuale non supporta annullamento automatico via web. Scrivici per modifiche o passa al nuovo piano.",
    // Account info
    accountInfo: "Informazioni account",
    company: "Azienda",
    phone: "Telefono",
    registeredOn: "Registrato il",
    // Payment history
    paymentHistory: "Storico pagamenti",
    paid: "Pagato",
    pending: "In attesa",
    // Cancel modal step 1
    beforeCancel: "Prima di annullare…",
    helpUsImprove: "Aiutaci a migliorare. Cosa ti porta ad annullare?",
    reasonPriceLabel: "Costo troppo alto",
    reasonPriceHint: "Possiamo proporti uno sconto?",
    reasonFeaturesLabel: "Mancano funzionalità che mi servono",
    reasonFeaturesHint: "Dimmi quali nello spazio sotto",
    reasonLowUsageLabel: "Lo uso poco / stagione bassa",
    reasonLowUsageHint: "Possiamo metterlo in pausa per qualche mese",
    reasonAlternativeLabel: "Ho trovato un'alternativa",
    reasonOtherLabel: "Altro motivo",
    notePlaceholderFeatures: "Quali funzionalità ti mancano?",
    notePlaceholderOther: "Raccontaci meglio…",
    back: "Indietro",
    continue: "Continua",
    // Cancel modal step 2
    cancelAutoRenewQuestion: "Annullare il rinnovo automatico?",
    cancelConfirmDescPart1: "Il tuo account resterà attivo fino al",
    cancelConfirmDescPart2: ". Non verrà addebitato alcun importo al rinnovo. Puoi riattivarlo in qualsiasi momento prima della scadenza.",
    pleaseWait: "Attendere…",
    confirmCancellation: "Conferma annullamento",
    // Reactivate modal
    reactivateQuestion: "Riattivare il rinnovo automatico?",
    reactivateDescPart1: "L'abbonamento verrà rinnovato automaticamente il",
    reactivateDescPart2: "con addebito di",
    cancel: "Annulla",
    confirmReactivation: "Conferma riattivazione",
    // Change plan modal
    switchToPlanQuestionAnnual: "Passare al piano Annuale?",
    switchToPlanQuestionMonthly: "Passare al piano Mensile?",
    changePlanAnnualPart1: "Passerai a",
    changePlanAnnualYear: "/anno",
    changePlanAnnualPart2: ". Risparmi",
    changePlanAnnualPart3: "rispetto al mensile.",
    changePlanMonthlyPart1: "Passerai a",
    changePlanMonthlyMonth: "/mese",
    changePlanMonthlyPart2: ". Eventuale credito residuo del piano annuale verrà accreditato sui prossimi mesi.",
    prorationPart1: "Stripe calcolerà automaticamente la differenza (",
    prorationWord: "proration",
    prorationPart2: ") e l'addebiterà o accrediterà sulla tua prossima fattura.",
    confirmChange: "Conferma cambio",
  },
  en: {
    metaPricingTitle: "Pricing · Nautiqo",
    metaPricingDesc: "One plan. Everything included. €19.90 per month or €199 per year. Cancel anytime.",
    navDashboard: "Dashboard",
    navLogin: "Log in",
    checkoutError: "Error during checkout. Please try again in a few seconds.",
    featUnlimitedBoats: "Unlimited boats",
    featCalendar: "Calendar and availability",
    featCustomers: "Customer and booking management",
    featPayments: "Stripe payments from your website",
    featNotifications: "Push notifications + email",
    featReports: "Reports and statistics",
    featAutomations: "Advanced automations",
    featStaff: "Multi-user staff",
    featIcal: "iCal synchronization",
    subscriptionLabel: "Subscription",
    heroTitle: "One plan. Everything included.",
    heroSubtitle: "Manage your charter without limits. Choose the billing frequency you prefer.",
    monthly: "Monthly",
    annual: "Annual",
    subActivatedTitle: "Subscription activated!",
    subActivatedDesc: "Your account is now active. Go to the dashboard to get started.",
    checkoutCancelled: "Checkout cancelled. You can try again anytime.",
    billingAnnual: "Annual billing",
    billingMonthly: "Monthly billing",
    perMonth: "/month",
    perYear: "/year",
    savePrefix: "You save",
    savePerYearSuffix: "per year",
    cancelAnytime: "Cancel anytime. No commitment.",
    redirectingStripe: "Redirecting to Stripe...",
    subscribeWithCard: "Subscribe with card",
    registerFirst: " — Register first",
    orFromApp: "or from the app",
    goToDashboard: "Go to the dashboard →",
    alreadyHaveAccount: "Already have an account?",
    securePayment: "Secure payment",
    cardsAccepted: "Visa, Mastercard, SEPA",
    cancelOneClick: "Cancel in 1 click",
    mobilePricingDisclaimer: "For purchases via the App Store (iPhone) or Google Play (Android), the price may vary based on your country and will be shown by the store at the time of purchase.",
    metaAccountTitle: "Account · Nautiqo",
    metaAccountDesc: "Manage your Nautiqo subscription.",
    toastSubActivatedTitle: "Subscription activated!",
    toastSubActivatedDesc: "Your account is now active. Welcome to Nautiqo.",
    toastCheckoutCancelledTitle: "Checkout cancelled",
    toastCheckoutCancelledDesc: "You can try again at any time.",
    toastAutoRenewCancelledTitle: "Auto-renewal cancelled",
    toastAutoRenewCancelledDesc1: "Your account will remain active until",
    toastCancelErrorTitle: "Error during cancellation",
    toastTryLater: "Please try again later.",
    toastReactivateAppTitle: "Reactivate from your Nautiqo app",
    toastReactivateAppDesc: "Open the app on your smartphone and renew your subscription from the App Store (iOS) or Google Play (Android).",
    toastChangePlanAppTitle: "Change plan from your Nautiqo app",
    toastChangePlanAppDesc: "Monthly/annual can be changed from the App Store (iOS) or Google Play (Android).",
    toastPlanUpdatedTitle: "Plan updated",
    toastSwitchedAnnual: "You switched to the annual plan.",
    toastSwitchedMonthly: "You switched to the monthly plan.",
    toastChangePlanErrorTitle: "Error changing plan",
    toastPortalErrorTitle: "Unable to open the portal",
    statusTrialActive: "Trial active",
    statusActive: "Active",
    statusRenewCancelled: "Renewal cancelled",
    statusPastDue: "Payment pending",
    statusExpired: "Expired",
    statusTrialExpired: "Trial expired",
    statusInactive: "Inactive",
    accountTitle: "Account",
    accountSubtitle: "Manage your Nautiqo subscription",
    subscription: "Subscription",
    plan: "Plan",
    trialExpiresOn: "Trial expires on",
    accessValidUntil: "Access valid until",
    nextRenewal: "Next renewal",
    expiredOn: "Expired on",
    daysSuffix: "days",
    channel: "Channel",
    email: "Email",
    cancelPendingPart1: "Auto-renewal was cancelled on",
    cancelPendingPart2: ". Your account will remain active until",
    cancelPendingPart3: ". You can reactivate renewal at any time before expiry.",
    trialUsingPart1: "You are using Nautiqo in",
    trialFreeTrial: "free trial",
    trialUsingPart2: " mode. You still have",
    trialUsingPart3: "to activate the plan and manage auto-renewal from the website.",
    manageSubscription: "Subscription management",
    manageDescApple: "Your subscription was activated through the App Store and can only be managed from your iPhone/iPad.",
    manageDescGoogle: "Your subscription was activated through Google Play and can only be managed from your Android device.",
    manageDescStripeActive: "Cancel auto-renewal — access remains active until expiry.",
    manageDescStripeCancelPending: "Reactivate auto-renewal to continue without interruption.",
    manageDescExpired: "Your subscription has expired. Reactivate it to get back up and running.",
    manageDescSetup: "Set up auto-renewal so you never lose access to the service.",
    manageDescLegacy: "You have an active subscription from the previous flow.",
    manageOnAppStore: "Manage on App Store",
    toModifyOrCancel: "To modify or cancel this subscription:",
    appleStep1Pre: "Open",
    appleStep1Bold: "Settings",
    appleStep1Post: "on your iPhone or iPad",
    appleStep2Pre: "Tap",
    appleStep2Bold: "your name",
    appleStep2Post: "at the top",
    appleStep3Pre: "Go to",
    appleStep3Bold: "Subscriptions",
    stepSelectPre: "Select",
    stepSelectPost: "and choose the desired action",
    openAppStoreSubs: "Open App Store Subscriptions",
    manageOnGooglePlay: "Manage on Google Play",
    googleStep1Pre: "Open the",
    googleStep1Bold: "Play Store",
    googleStep1Post: "app on Android",
    googleStep2Pre: "Tap",
    googleStep2Bold: "your profile",
    googleStep2Post: "at the top right",
    googleStep3Pre: "Go to",
    googleStep3Bold: "Payments and subscriptions → Subscriptions",
    openGooglePlaySubs: "Open Google Play Subscriptions",
    switchTo: "Switch to",
    updatePaymentInvoices: "Update payment and invoices",
    cancelAutoRenew: "Cancel auto-renewal",
    accountActiveUntilPart1: "Your account stays active until",
    accountActiveUntilPart2: ". No charge at renewal.",
    reactivateAutoRenew: "Reactivate auto-renewal",
    willBeChargedPart1: "You will be charged",
    willBeChargedPart2Day: "on",
    reactivateSubscription: "Reactivate subscription",
    setupAutoRenew: "Set up auto-renewal",
    chooseMonthlyAnnual: "Choose Monthly or Annual. Cancel anytime, no commitment.",
    contactSupport: "Contact support",
    switchToNewPlan: "Switch to the new plan with auto-renewal",
    legacyInfo: "Your current plan does not support automatic cancellation via web. Write to us for changes or switch to the new plan.",
    accountInfo: "Account information",
    company: "Company",
    phone: "Phone",
    registeredOn: "Registered on",
    paymentHistory: "Payment history",
    paid: "Paid",
    pending: "Pending",
    beforeCancel: "Before you cancel…",
    helpUsImprove: "Help us improve. What's making you cancel?",
    reasonPriceLabel: "Too expensive",
    reasonPriceHint: "Can we offer you a discount?",
    reasonFeaturesLabel: "Missing features I need",
    reasonFeaturesHint: "Tell us which ones in the space below",
    reasonLowUsageLabel: "I use it little / low season",
    reasonLowUsageHint: "We can pause it for a few months",
    reasonAlternativeLabel: "I found an alternative",
    reasonOtherLabel: "Other reason",
    notePlaceholderFeatures: "Which features are you missing?",
    notePlaceholderOther: "Tell us more…",
    back: "Back",
    continue: "Continue",
    cancelAutoRenewQuestion: "Cancel auto-renewal?",
    cancelConfirmDescPart1: "Your account will remain active until",
    cancelConfirmDescPart2: ". No amount will be charged at renewal. You can reactivate it at any time before expiry.",
    pleaseWait: "Please wait…",
    confirmCancellation: "Confirm cancellation",
    reactivateQuestion: "Reactivate auto-renewal?",
    reactivateDescPart1: "The subscription will renew automatically on",
    reactivateDescPart2: "with a charge of",
    cancel: "Cancel",
    confirmReactivation: "Confirm reactivation",
    switchToPlanQuestionAnnual: "Switch to the Annual plan?",
    switchToPlanQuestionMonthly: "Switch to the Monthly plan?",
    changePlanAnnualPart1: "You will switch to",
    changePlanAnnualYear: "/year",
    changePlanAnnualPart2: ". You save",
    changePlanAnnualPart3: "compared to monthly.",
    changePlanMonthlyPart1: "You will switch to",
    changePlanMonthlyMonth: "/month",
    changePlanMonthlyPart2: ". Any remaining credit from the annual plan will be applied to the coming months.",
    prorationPart1: "Stripe will automatically calculate the difference (",
    prorationWord: "proration",
    prorationPart2: ") and charge or credit it on your next invoice.",
    confirmChange: "Confirm change",
  },
  es: {
    metaPricingTitle: "Precios · Nautiqo",
    metaPricingDesc: "Un plan. Todo incluido. €19,90 al mes o €199 al año. Cancela cuando quieras.",
    navDashboard: "Panel",
    navLogin: "Iniciar sesión",
    checkoutError: "Error durante el pago. Inténtalo de nuevo en unos segundos.",
    featUnlimitedBoats: "Barcos ilimitados",
    featCalendar: "Calendario y disponibilidad",
    featCustomers: "Gestión de clientes y reservas",
    featPayments: "Pagos Stripe desde tu sitio web",
    featNotifications: "Notificaciones push + correo",
    featReports: "Informes y estadísticas",
    featAutomations: "Automatizaciones avanzadas",
    featStaff: "Personal multiusuario",
    featIcal: "Sincronización iCal",
    subscriptionLabel: "Suscripción",
    heroTitle: "Un plan. Todo incluido.",
    heroSubtitle: "Gestiona tu charter sin límites. Elige la frecuencia de facturación que prefieras.",
    monthly: "Mensual",
    annual: "Anual",
    subActivatedTitle: "¡Suscripción activada!",
    subActivatedDesc: "Tu cuenta ya está activa. Ve al panel para empezar.",
    checkoutCancelled: "Pago cancelado. Puedes volver a intentarlo cuando quieras.",
    billingAnnual: "Facturación anual",
    billingMonthly: "Facturación mensual",
    perMonth: "/mes",
    perYear: "/año",
    savePrefix: "Ahorras",
    savePerYearSuffix: "al año",
    cancelAnytime: "Cancela cuando quieras. Sin compromiso.",
    redirectingStripe: "Redirigiendo a Stripe...",
    subscribeWithCard: "Suscríbete con tarjeta",
    registerFirst: " — Regístrate primero",
    orFromApp: "o desde la app",
    goToDashboard: "Ir al panel →",
    alreadyHaveAccount: "¿Ya tienes una cuenta?",
    securePayment: "Pago seguro",
    cardsAccepted: "Visa, Mastercard, SEPA",
    cancelOneClick: "Cancela en 1 clic",
    mobilePricingDisclaimer: "Para compras a través de App Store (iPhone) o Google Play (Android), el precio puede variar según tu país y se mostrará en la tienda en el momento de la compra.",
    metaAccountTitle: "Cuenta · Nautiqo",
    metaAccountDesc: "Gestiona tu suscripción a Nautiqo.",
    toastSubActivatedTitle: "¡Suscripción activada!",
    toastSubActivatedDesc: "Tu cuenta ya está activa. Bienvenido a Nautiqo.",
    toastCheckoutCancelledTitle: "Pago cancelado",
    toastCheckoutCancelledDesc: "Puedes volver a intentarlo en cualquier momento.",
    toastAutoRenewCancelledTitle: "Renovación automática cancelada",
    toastAutoRenewCancelledDesc1: "Tu cuenta permanecerá activa hasta el",
    toastCancelErrorTitle: "Error durante la cancelación",
    toastTryLater: "Inténtalo de nuevo más tarde.",
    toastReactivateAppTitle: "Reactiva desde tu app Nautiqo",
    toastReactivateAppDesc: "Abre la app en tu smartphone y renueva la suscripción desde App Store (iOS) o Google Play (Android).",
    toastChangePlanAppTitle: "Cambia de plan desde tu app Nautiqo",
    toastChangePlanAppDesc: "Mensual/anual se pueden modificar desde App Store (iOS) o Google Play (Android).",
    toastPlanUpdatedTitle: "Plan actualizado",
    toastSwitchedAnnual: "Has cambiado al plan anual.",
    toastSwitchedMonthly: "Has cambiado al plan mensual.",
    toastChangePlanErrorTitle: "Error al cambiar de plan",
    toastPortalErrorTitle: "No se pudo abrir el portal",
    statusTrialActive: "Prueba activa",
    statusActive: "Activo",
    statusRenewCancelled: "Renovación cancelada",
    statusPastDue: "Pago pendiente",
    statusExpired: "Caducado",
    statusTrialExpired: "Prueba caducada",
    statusInactive: "Inactivo",
    accountTitle: "Cuenta",
    accountSubtitle: "Gestiona tu suscripción a Nautiqo",
    subscription: "Suscripción",
    plan: "Plan",
    trialExpiresOn: "La prueba caduca el",
    accessValidUntil: "Acceso válido hasta el",
    nextRenewal: "Próxima renovación",
    expiredOn: "Caducado el",
    daysSuffix: "días",
    channel: "Canal",
    email: "Correo electrónico",
    cancelPendingPart1: "La renovación automática se canceló el",
    cancelPendingPart2: ". Tu cuenta permanecerá activa hasta el",
    cancelPendingPart3: ". Puedes reactivar la renovación en cualquier momento antes del vencimiento.",
    trialUsingPart1: "Estás usando Nautiqo en modo",
    trialFreeTrial: "prueba gratuita",
    trialUsingPart2: ". Aún tienes",
    trialUsingPart3: "para activar el plan y gestionar la renovación automática desde el sitio web.",
    manageSubscription: "Gestión de la suscripción",
    manageDescApple: "Tu suscripción se activó a través de App Store y solo se puede gestionar desde tu iPhone/iPad.",
    manageDescGoogle: "Tu suscripción se activó a través de Google Play y solo se puede gestionar desde tu dispositivo Android.",
    manageDescStripeActive: "Cancela la renovación automática — el acceso permanece activo hasta el vencimiento.",
    manageDescStripeCancelPending: "Reactiva la renovación automática para continuar sin interrupciones.",
    manageDescExpired: "Tu suscripción ha caducado. Reactívala para volver a operar.",
    manageDescSetup: "Configura la renovación automática para no perder nunca el acceso al servicio.",
    manageDescLegacy: "Tienes una suscripción activa del flujo anterior.",
    manageOnAppStore: "Gestionar en App Store",
    toModifyOrCancel: "Para modificar o cancelar esta suscripción:",
    appleStep1Pre: "Abre",
    appleStep1Bold: "Ajustes",
    appleStep1Post: "en tu iPhone o iPad",
    appleStep2Pre: "Toca",
    appleStep2Bold: "tu nombre",
    appleStep2Post: "en la parte superior",
    appleStep3Pre: "Ve a",
    appleStep3Bold: "Suscripciones",
    stepSelectPre: "Selecciona",
    stepSelectPost: "y elige la acción deseada",
    openAppStoreSubs: "Abrir Suscripciones de App Store",
    manageOnGooglePlay: "Gestionar en Google Play",
    googleStep1Pre: "Abre la app",
    googleStep1Bold: "Play Store",
    googleStep1Post: "en Android",
    googleStep2Pre: "Toca",
    googleStep2Bold: "tu perfil",
    googleStep2Post: "arriba a la derecha",
    googleStep3Pre: "Ve a",
    googleStep3Bold: "Pagos y suscripciones → Suscripciones",
    openGooglePlaySubs: "Abrir Suscripciones de Google Play",
    switchTo: "Cambiar a",
    updatePaymentInvoices: "Actualizar pago y facturas",
    cancelAutoRenew: "Cancelar renovación automática",
    accountActiveUntilPart1: "Tu cuenta permanece activa hasta el",
    accountActiveUntilPart2: ". Sin cargo en la renovación.",
    reactivateAutoRenew: "Reactivar renovación automática",
    willBeChargedPart1: "Se cobrará",
    willBeChargedPart2Day: "el",
    reactivateSubscription: "Reactivar suscripción",
    setupAutoRenew: "Configurar renovación automática",
    chooseMonthlyAnnual: "Elige Mensual o Anual. Cancela cuando quieras, sin compromiso.",
    contactSupport: "Contactar con soporte",
    switchToNewPlan: "Cambiar al nuevo plan con renovación automática",
    legacyInfo: "Tu plan actual no admite cancelación automática vía web. Escríbenos para cambios o cambia al nuevo plan.",
    accountInfo: "Información de la cuenta",
    company: "Empresa",
    phone: "Teléfono",
    registeredOn: "Registrado el",
    paymentHistory: "Historial de pagos",
    paid: "Pagado",
    pending: "Pendiente",
    beforeCancel: "Antes de cancelar…",
    helpUsImprove: "Ayúdanos a mejorar. ¿Qué te lleva a cancelar?",
    reasonPriceLabel: "Demasiado caro",
    reasonPriceHint: "¿Podemos ofrecerte un descuento?",
    reasonFeaturesLabel: "Faltan funciones que necesito",
    reasonFeaturesHint: "Dinos cuáles en el espacio de abajo",
    reasonLowUsageLabel: "Lo uso poco / temporada baja",
    reasonLowUsageHint: "Podemos pausarlo durante unos meses",
    reasonAlternativeLabel: "He encontrado una alternativa",
    reasonOtherLabel: "Otro motivo",
    notePlaceholderFeatures: "¿Qué funciones te faltan?",
    notePlaceholderOther: "Cuéntanos más…",
    back: "Atrás",
    continue: "Continuar",
    cancelAutoRenewQuestion: "¿Cancelar la renovación automática?",
    cancelConfirmDescPart1: "Tu cuenta permanecerá activa hasta el",
    cancelConfirmDescPart2: ". No se cobrará ningún importe en la renovación. Puedes reactivarla en cualquier momento antes del vencimiento.",
    pleaseWait: "Espera…",
    confirmCancellation: "Confirmar cancelación",
    reactivateQuestion: "¿Reactivar la renovación automática?",
    reactivateDescPart1: "La suscripción se renovará automáticamente el",
    reactivateDescPart2: "con un cargo de",
    cancel: "Cancelar",
    confirmReactivation: "Confirmar reactivación",
    switchToPlanQuestionAnnual: "¿Cambiar al plan Anual?",
    switchToPlanQuestionMonthly: "¿Cambiar al plan Mensual?",
    changePlanAnnualPart1: "Cambiarás a",
    changePlanAnnualYear: "/año",
    changePlanAnnualPart2: ". Ahorras",
    changePlanAnnualPart3: "respecto al mensual.",
    changePlanMonthlyPart1: "Cambiarás a",
    changePlanMonthlyMonth: "/mes",
    changePlanMonthlyPart2: ". Cualquier crédito restante del plan anual se aplicará a los próximos meses.",
    prorationPart1: "Stripe calculará automáticamente la diferencia (",
    prorationWord: "prorrateo",
    prorationPart2: ") y la cobrará o acreditará en tu próxima factura.",
    confirmChange: "Confirmar cambio",
  },
  fr: {
    metaPricingTitle: "Tarifs · Nautiqo",
    metaPricingDesc: "Un forfait. Tout compris. 19,90 €/mois ou 199 €/an. Annulez à tout moment.",
    navDashboard: "Tableau de bord",
    navLogin: "Se connecter",
    checkoutError: "Erreur lors du paiement. Veuillez réessayer dans quelques secondes.",
    featUnlimitedBoats: "Bateaux illimités",
    featCalendar: "Calendrier et disponibilité",
    featCustomers: "Gestion des clients et des réservations",
    featPayments: "Paiements Stripe depuis votre site",
    featNotifications: "Notifications push + e-mail",
    featReports: "Rapports et statistiques",
    featAutomations: "Automatisations avancées",
    featStaff: "Équipe multi-utilisateurs",
    featIcal: "Synchronisation iCal",
    subscriptionLabel: "Abonnement",
    heroTitle: "Un forfait. Tout compris.",
    heroSubtitle: "Gérez votre charter sans limites. Choisissez la fréquence de facturation que vous préférez.",
    monthly: "Mensuel",
    annual: "Annuel",
    subActivatedTitle: "Abonnement activé !",
    subActivatedDesc: "Votre compte est désormais actif. Accédez au tableau de bord pour commencer.",
    checkoutCancelled: "Paiement annulé. Vous pouvez réessayer quand vous le souhaitez.",
    billingAnnual: "Facturation annuelle",
    billingMonthly: "Facturation mensuelle",
    perMonth: "/mois",
    perYear: "/an",
    savePrefix: "Vous économisez",
    savePerYearSuffix: "par an",
    cancelAnytime: "Annulez à tout moment. Sans engagement.",
    redirectingStripe: "Redirection vers Stripe...",
    subscribeWithCard: "S'abonner par carte",
    registerFirst: " — Inscrivez-vous d'abord",
    orFromApp: "ou depuis l'app",
    goToDashboard: "Accéder au tableau de bord →",
    alreadyHaveAccount: "Vous avez déjà un compte ?",
    securePayment: "Paiement sécurisé",
    cardsAccepted: "Visa, Mastercard, SEPA",
    cancelOneClick: "Annulez en 1 clic",
    mobilePricingDisclaimer: "Pour les achats via l'App Store (iPhone) ou Google Play (Android), le prix peut varier selon votre pays et sera affiché par le store au moment de l'achat.",
    metaAccountTitle: "Compte · Nautiqo",
    metaAccountDesc: "Gérez votre abonnement Nautiqo.",
    toastSubActivatedTitle: "Abonnement activé !",
    toastSubActivatedDesc: "Votre compte est désormais actif. Bienvenue sur Nautiqo.",
    toastCheckoutCancelledTitle: "Paiement annulé",
    toastCheckoutCancelledDesc: "Vous pouvez réessayer à tout moment.",
    toastAutoRenewCancelledTitle: "Renouvellement automatique annulé",
    toastAutoRenewCancelledDesc1: "Votre compte restera actif jusqu'au",
    toastCancelErrorTitle: "Erreur lors de l'annulation",
    toastTryLater: "Veuillez réessayer plus tard.",
    toastReactivateAppTitle: "Réactivez depuis votre app Nautiqo",
    toastReactivateAppDesc: "Ouvrez l'app sur votre smartphone et renouvelez l'abonnement depuis l'App Store (iOS) ou Google Play (Android).",
    toastChangePlanAppTitle: "Changez de forfait depuis votre app Nautiqo",
    toastChangePlanAppDesc: "Mensuel/annuel peuvent être modifiés depuis l'App Store (iOS) ou Google Play (Android).",
    toastPlanUpdatedTitle: "Forfait mis à jour",
    toastSwitchedAnnual: "Vous êtes passé au forfait annuel.",
    toastSwitchedMonthly: "Vous êtes passé au forfait mensuel.",
    toastChangePlanErrorTitle: "Erreur lors du changement de forfait",
    toastPortalErrorTitle: "Impossible d'ouvrir le portail",
    statusTrialActive: "Essai actif",
    statusActive: "Actif",
    statusRenewCancelled: "Renouvellement annulé",
    statusPastDue: "Paiement en attente",
    statusExpired: "Expiré",
    statusTrialExpired: "Essai expiré",
    statusInactive: "Inactif",
    accountTitle: "Compte",
    accountSubtitle: "Gérez votre abonnement Nautiqo",
    subscription: "Abonnement",
    plan: "Forfait",
    trialExpiresOn: "L'essai expire le",
    accessValidUntil: "Accès valable jusqu'au",
    nextRenewal: "Prochain renouvellement",
    expiredOn: "Expiré le",
    daysSuffix: "jours",
    channel: "Canal",
    email: "E-mail",
    cancelPendingPart1: "Le renouvellement automatique a été annulé le",
    cancelPendingPart2: ". Votre compte restera actif jusqu'au",
    cancelPendingPart3: ". Vous pouvez réactiver le renouvellement à tout moment avant l'échéance.",
    trialUsingPart1: "Vous utilisez Nautiqo en mode",
    trialFreeTrial: "essai gratuit",
    trialUsingPart2: ". Il vous reste encore",
    trialUsingPart3: "pour activer le forfait et gérer le renouvellement automatique depuis le site.",
    manageSubscription: "Gestion de l'abonnement",
    manageDescApple: "Votre abonnement a été activé via l'App Store et ne peut être géré que depuis votre iPhone/iPad.",
    manageDescGoogle: "Votre abonnement a été activé via Google Play et ne peut être géré que depuis votre appareil Android.",
    manageDescStripeActive: "Annulez le renouvellement automatique — l'accès reste actif jusqu'à l'échéance.",
    manageDescStripeCancelPending: "Réactivez le renouvellement automatique pour continuer sans interruption.",
    manageDescExpired: "Votre abonnement a expiré. Réactivez-le pour redevenir opérationnel.",
    manageDescSetup: "Configurez le renouvellement automatique pour ne jamais perdre l'accès au service.",
    manageDescLegacy: "Vous avez un abonnement actif issu du flux précédent.",
    manageOnAppStore: "Gérer sur l'App Store",
    toModifyOrCancel: "Pour modifier ou annuler cet abonnement :",
    appleStep1Pre: "Ouvrez",
    appleStep1Bold: "Réglages",
    appleStep1Post: "sur votre iPhone ou iPad",
    appleStep2Pre: "Touchez",
    appleStep2Bold: "votre nom",
    appleStep2Post: "en haut",
    appleStep3Pre: "Allez dans",
    appleStep3Bold: "Abonnements",
    stepSelectPre: "Sélectionnez",
    stepSelectPost: "et choisissez l'action souhaitée",
    openAppStoreSubs: "Ouvrir les Abonnements App Store",
    manageOnGooglePlay: "Gérer sur Google Play",
    googleStep1Pre: "Ouvrez l'app",
    googleStep1Bold: "Play Store",
    googleStep1Post: "sur Android",
    googleStep2Pre: "Touchez",
    googleStep2Bold: "votre profil",
    googleStep2Post: "en haut à droite",
    googleStep3Pre: "Allez dans",
    googleStep3Bold: "Paiements et abonnements → Abonnements",
    openGooglePlaySubs: "Ouvrir les Abonnements Google Play",
    switchTo: "Passer à",
    updatePaymentInvoices: "Mettre à jour le paiement et les factures",
    cancelAutoRenew: "Annuler le renouvellement automatique",
    accountActiveUntilPart1: "Votre compte reste actif jusqu'au",
    accountActiveUntilPart2: ". Aucun débit au renouvellement.",
    reactivateAutoRenew: "Réactiver le renouvellement automatique",
    willBeChargedPart1: "Un montant de",
    willBeChargedPart2Day: "sera débité le",
    reactivateSubscription: "Réactiver l'abonnement",
    setupAutoRenew: "Configurer le renouvellement automatique",
    chooseMonthlyAnnual: "Choisissez Mensuel ou Annuel. Annulez quand vous voulez, sans engagement.",
    contactSupport: "Contacter l'assistance",
    switchToNewPlan: "Passer au nouveau forfait avec renouvellement automatique",
    legacyInfo: "Votre forfait actuel ne prend pas en charge l'annulation automatique via le web. Écrivez-nous pour toute modification ou passez au nouveau forfait.",
    accountInfo: "Informations du compte",
    company: "Entreprise",
    phone: "Téléphone",
    registeredOn: "Inscrit le",
    paymentHistory: "Historique des paiements",
    paid: "Payé",
    pending: "En attente",
    beforeCancel: "Avant d'annuler…",
    helpUsImprove: "Aidez-nous à nous améliorer. Qu'est-ce qui vous pousse à annuler ?",
    reasonPriceLabel: "Trop cher",
    reasonPriceHint: "Pouvons-nous vous proposer une remise ?",
    reasonFeaturesLabel: "Il manque des fonctionnalités dont j'ai besoin",
    reasonFeaturesHint: "Dites-nous lesquelles dans l'espace ci-dessous",
    reasonLowUsageLabel: "Je l'utilise peu / basse saison",
    reasonLowUsageHint: "Nous pouvons le mettre en pause quelques mois",
    reasonAlternativeLabel: "J'ai trouvé une alternative",
    reasonOtherLabel: "Autre raison",
    notePlaceholderFeatures: "Quelles fonctionnalités vous manquent ?",
    notePlaceholderOther: "Dites-nous-en plus…",
    back: "Retour",
    continue: "Continuer",
    cancelAutoRenewQuestion: "Annuler le renouvellement automatique ?",
    cancelConfirmDescPart1: "Votre compte restera actif jusqu'au",
    cancelConfirmDescPart2: ". Aucun montant ne sera débité au renouvellement. Vous pouvez le réactiver à tout moment avant l'échéance.",
    pleaseWait: "Veuillez patienter…",
    confirmCancellation: "Confirmer l'annulation",
    reactivateQuestion: "Réactiver le renouvellement automatique ?",
    reactivateDescPart1: "L'abonnement sera renouvelé automatiquement le",
    reactivateDescPart2: "avec un débit de",
    cancel: "Annuler",
    confirmReactivation: "Confirmer la réactivation",
    switchToPlanQuestionAnnual: "Passer au forfait Annuel ?",
    switchToPlanQuestionMonthly: "Passer au forfait Mensuel ?",
    changePlanAnnualPart1: "Vous passerez à",
    changePlanAnnualYear: "/an",
    changePlanAnnualPart2: ". Vous économisez",
    changePlanAnnualPart3: "par rapport au mensuel.",
    changePlanMonthlyPart1: "Vous passerez à",
    changePlanMonthlyMonth: "/mois",
    changePlanMonthlyPart2: ". Tout crédit restant du forfait annuel sera appliqué aux mois suivants.",
    prorationPart1: "Stripe calculera automatiquement la différence (",
    prorationWord: "prorata",
    prorationPart2: ") et la débitera ou la créditera sur votre prochaine facture.",
    confirmChange: "Confirmer le changement",
  },
  de: {
    metaPricingTitle: "Preise · Nautiqo",
    metaPricingDesc: "Ein Tarif. Alles inklusive. 19,90 € im Monat oder 199 € im Jahr. Jederzeit kündbar.",
    navDashboard: "Dashboard",
    navLogin: "Anmelden",
    checkoutError: "Fehler beim Bezahlvorgang. Bitte versuche es in ein paar Sekunden erneut.",
    featUnlimitedBoats: "Unbegrenzte Boote",
    featCalendar: "Kalender und Verfügbarkeit",
    featCustomers: "Kunden- und Buchungsverwaltung",
    featPayments: "Stripe-Zahlungen von deiner Website",
    featNotifications: "Push-Benachrichtigungen + E-Mail",
    featReports: "Berichte und Statistiken",
    featAutomations: "Erweiterte Automatisierungen",
    featStaff: "Mehrbenutzer-Team",
    featIcal: "iCal-Synchronisierung",
    subscriptionLabel: "Abonnement",
    heroTitle: "Ein Tarif. Alles inklusive.",
    heroSubtitle: "Verwalte deinen Charter ohne Grenzen. Wähle den Abrechnungszeitraum, den du bevorzugst.",
    monthly: "Monatlich",
    annual: "Jährlich",
    subActivatedTitle: "Abonnement aktiviert!",
    subActivatedDesc: "Dein Konto ist jetzt aktiv. Gehe zum Dashboard, um zu starten.",
    checkoutCancelled: "Bezahlvorgang abgebrochen. Du kannst es jederzeit erneut versuchen.",
    billingAnnual: "Jährliche Abrechnung",
    billingMonthly: "Monatliche Abrechnung",
    perMonth: "/Monat",
    perYear: "/Jahr",
    savePrefix: "Du sparst",
    savePerYearSuffix: "pro Jahr",
    cancelAnytime: "Jederzeit kündbar. Keine Bindung.",
    redirectingStripe: "Weiterleitung zu Stripe...",
    subscribeWithCard: "Mit Karte abonnieren",
    registerFirst: " — Zuerst registrieren",
    orFromApp: "oder über die App",
    goToDashboard: "Zum Dashboard →",
    alreadyHaveAccount: "Hast du bereits ein Konto?",
    securePayment: "Sichere Zahlung",
    cardsAccepted: "Visa, Mastercard, SEPA",
    cancelOneClick: "Kündigung mit 1 Klick",
    mobilePricingDisclaimer: "Bei Käufen über den App Store (iPhone) oder Google Play (Android) kann der Preis je nach Land variieren und wird vom Store zum Zeitpunkt des Kaufs angezeigt.",
    metaAccountTitle: "Konto · Nautiqo",
    metaAccountDesc: "Verwalte dein Nautiqo-Abonnement.",
    toastSubActivatedTitle: "Abonnement aktiviert!",
    toastSubActivatedDesc: "Dein Konto ist jetzt aktiv. Willkommen bei Nautiqo.",
    toastCheckoutCancelledTitle: "Bezahlvorgang abgebrochen",
    toastCheckoutCancelledDesc: "Du kannst es jederzeit erneut versuchen.",
    toastAutoRenewCancelledTitle: "Automatische Verlängerung gekündigt",
    toastAutoRenewCancelledDesc1: "Dein Konto bleibt aktiv bis zum",
    toastCancelErrorTitle: "Fehler bei der Kündigung",
    toastTryLater: "Bitte versuche es später erneut.",
    toastReactivateAppTitle: "Reaktiviere über deine Nautiqo-App",
    toastReactivateAppDesc: "Öffne die App auf deinem Smartphone und verlängere das Abonnement über den App Store (iOS) oder Google Play (Android).",
    toastChangePlanAppTitle: "Wechsle den Tarif über deine Nautiqo-App",
    toastChangePlanAppDesc: "Monatlich/jährlich kann über den App Store (iOS) oder Google Play (Android) geändert werden.",
    toastPlanUpdatedTitle: "Tarif aktualisiert",
    toastSwitchedAnnual: "Du bist zum Jahrestarif gewechselt.",
    toastSwitchedMonthly: "Du bist zum Monatstarif gewechselt.",
    toastChangePlanErrorTitle: "Fehler beim Tarifwechsel",
    toastPortalErrorTitle: "Portal konnte nicht geöffnet werden",
    statusTrialActive: "Testphase aktiv",
    statusActive: "Aktiv",
    statusRenewCancelled: "Verlängerung gekündigt",
    statusPastDue: "Zahlung ausstehend",
    statusExpired: "Abgelaufen",
    statusTrialExpired: "Testphase abgelaufen",
    statusInactive: "Inaktiv",
    accountTitle: "Konto",
    accountSubtitle: "Verwalte dein Nautiqo-Abonnement",
    subscription: "Abonnement",
    plan: "Tarif",
    trialExpiresOn: "Testphase läuft ab am",
    accessValidUntil: "Zugang gültig bis",
    nextRenewal: "Nächste Verlängerung",
    expiredOn: "Abgelaufen am",
    daysSuffix: "Tage",
    channel: "Kanal",
    email: "E-Mail",
    cancelPendingPart1: "Die automatische Verlängerung wurde gekündigt am",
    cancelPendingPart2: ". Dein Konto bleibt aktiv bis zum",
    cancelPendingPart3: ". Du kannst die Verlängerung jederzeit vor Ablauf reaktivieren.",
    trialUsingPart1: "Du nutzt Nautiqo im Modus",
    trialFreeTrial: "kostenlose Testversion",
    trialUsingPart2: ". Du hast noch",
    trialUsingPart3: ", um den Tarif zu aktivieren und die automatische Verlängerung über die Website zu verwalten.",
    manageSubscription: "Abonnementverwaltung",
    manageDescApple: "Dein Abonnement wurde über den App Store aktiviert und kann nur über dein iPhone/iPad verwaltet werden.",
    manageDescGoogle: "Dein Abonnement wurde über Google Play aktiviert und kann nur über dein Android-Gerät verwaltet werden.",
    manageDescStripeActive: "Kündige die automatische Verlängerung — der Zugang bleibt bis zum Ablauf aktiv.",
    manageDescStripeCancelPending: "Reaktiviere die automatische Verlängerung, um ohne Unterbrechung fortzufahren.",
    manageDescExpired: "Dein Abonnement ist abgelaufen. Reaktiviere es, um wieder einsatzbereit zu sein.",
    manageDescSetup: "Richte die automatische Verlängerung ein, damit du nie den Zugang zum Dienst verlierst.",
    manageDescLegacy: "Du hast ein aktives Abonnement aus dem vorherigen Ablauf.",
    manageOnAppStore: "Im App Store verwalten",
    toModifyOrCancel: "Um dieses Abonnement zu ändern oder zu kündigen:",
    appleStep1Pre: "Öffne",
    appleStep1Bold: "Einstellungen",
    appleStep1Post: "auf deinem iPhone oder iPad",
    appleStep2Pre: "Tippe auf",
    appleStep2Bold: "deinen Namen",
    appleStep2Post: "ganz oben",
    appleStep3Pre: "Gehe zu",
    appleStep3Bold: "Abonnements",
    stepSelectPre: "Wähle",
    stepSelectPost: "und wähle die gewünschte Aktion",
    openAppStoreSubs: "App-Store-Abonnements öffnen",
    manageOnGooglePlay: "In Google Play verwalten",
    googleStep1Pre: "Öffne die App",
    googleStep1Bold: "Play Store",
    googleStep1Post: "auf Android",
    googleStep2Pre: "Tippe auf",
    googleStep2Bold: "dein Profil",
    googleStep2Post: "oben rechts",
    googleStep3Pre: "Gehe zu",
    googleStep3Bold: "Zahlungen und Abos → Abos",
    openGooglePlaySubs: "Google-Play-Abos öffnen",
    switchTo: "Wechseln zu",
    updatePaymentInvoices: "Zahlung und Rechnungen aktualisieren",
    cancelAutoRenew: "Automatische Verlängerung kündigen",
    accountActiveUntilPart1: "Dein Konto bleibt aktiv bis zum",
    accountActiveUntilPart2: ". Keine Abbuchung bei der Verlängerung.",
    reactivateAutoRenew: "Automatische Verlängerung reaktivieren",
    willBeChargedPart1: "Es werden",
    willBeChargedPart2Day: "abgebucht am",
    reactivateSubscription: "Abonnement reaktivieren",
    setupAutoRenew: "Automatische Verlängerung einrichten",
    chooseMonthlyAnnual: "Wähle Monatlich oder Jährlich. Jederzeit kündbar, keine Bindung.",
    contactSupport: "Support kontaktieren",
    switchToNewPlan: "Zum neuen Tarif mit automatischer Verlängerung wechseln",
    legacyInfo: "Dein aktueller Tarif unterstützt keine automatische Kündigung über das Web. Schreib uns für Änderungen oder wechsle zum neuen Tarif.",
    accountInfo: "Kontoinformationen",
    company: "Unternehmen",
    phone: "Telefon",
    registeredOn: "Registriert am",
    paymentHistory: "Zahlungsverlauf",
    paid: "Bezahlt",
    pending: "Ausstehend",
    beforeCancel: "Bevor du kündigst…",
    helpUsImprove: "Hilf uns, besser zu werden. Was bringt dich zur Kündigung?",
    reasonPriceLabel: "Zu teuer",
    reasonPriceHint: "Können wir dir einen Rabatt anbieten?",
    reasonFeaturesLabel: "Es fehlen Funktionen, die ich brauche",
    reasonFeaturesHint: "Sag uns im Feld unten, welche",
    reasonLowUsageLabel: "Ich nutze es wenig / Nebensaison",
    reasonLowUsageHint: "Wir können es für ein paar Monate pausieren",
    reasonAlternativeLabel: "Ich habe eine Alternative gefunden",
    reasonOtherLabel: "Anderer Grund",
    notePlaceholderFeatures: "Welche Funktionen fehlen dir?",
    notePlaceholderOther: "Erzähl uns mehr…",
    back: "Zurück",
    continue: "Weiter",
    cancelAutoRenewQuestion: "Automatische Verlängerung kündigen?",
    cancelConfirmDescPart1: "Dein Konto bleibt aktiv bis zum",
    cancelConfirmDescPart2: ". Bei der Verlängerung wird kein Betrag abgebucht. Du kannst sie jederzeit vor Ablauf reaktivieren.",
    pleaseWait: "Bitte warten…",
    confirmCancellation: "Kündigung bestätigen",
    reactivateQuestion: "Automatische Verlängerung reaktivieren?",
    reactivateDescPart1: "Das Abonnement wird automatisch verlängert am",
    reactivateDescPart2: "mit einer Abbuchung von",
    cancel: "Abbrechen",
    confirmReactivation: "Reaktivierung bestätigen",
    switchToPlanQuestionAnnual: "Zum Jahrestarif wechseln?",
    switchToPlanQuestionMonthly: "Zum Monatstarif wechseln?",
    changePlanAnnualPart1: "Du wechselst zu",
    changePlanAnnualYear: "/Jahr",
    changePlanAnnualPart2: ". Du sparst",
    changePlanAnnualPart3: "gegenüber dem Monatstarif.",
    changePlanMonthlyPart1: "Du wechselst zu",
    changePlanMonthlyMonth: "/Monat",
    changePlanMonthlyPart2: ". Ein eventuelles Restguthaben aus dem Jahrestarif wird auf die kommenden Monate angerechnet.",
    prorationPart1: "Stripe berechnet automatisch die Differenz (",
    prorationWord: "anteilige Abrechnung",
    prorationPart2: ") und bucht sie ab oder schreibt sie auf deiner nächsten Rechnung gut.",
    confirmChange: "Änderung bestätigen",
  },
};
const LOGO_URL = "https://nautiqo.cloud/logo-nautiqo.png";
const heading = { fontFamily: "'Cabinet Grotesk', 'Outfit', sans-serif" };
const body = { fontFamily: "'Manrope', sans-serif" };

// ==================== PRICING PAGE ====================
export const PricingPage = () => {
  const [billing, setBilling] = useState('annual');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const token = localStorage.getItem('nautiqo_token');
  const [searchParams] = useSearchParams();
  const checkoutResult = searchParams.get('checkout');
  const t = usePageT(translations);
  usePageMeta({
    title: t.metaPricingTitle,
    description: t.metaPricingDesc,
  });

  const handleCheckout = async () => {
    if (!token) { navigate('/register'); return; }
    setLoading(true);
    setError('');
    try {
      const billingCycle = billing === 'annual' ? 'yearly' : 'monthly';
      const { data } = await axios.post(
        `${API}/api/create-checkout-session`,
        { plan: 'pro', billing_cycle: billingCycle },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.location.href = data.url;
    } catch (err) {
      setError(err.response?.data?.detail || t.checkoutError);
      setLoading(false);
    }
  };

  const features = [
    { icon: Ship, text: t.featUnlimitedBoats },
    { icon: CalendarCheck, text: t.featCalendar },
    { icon: Users, text: t.featCustomers },
    { icon: CreditCard, text: t.featPayments },
    { icon: Bell, text: t.featNotifications },
    { icon: BarChart3, text: t.featReports },
    { icon: Zap, text: t.featAutomations },
    { icon: Shield, text: t.featStaff },
    { icon: Clock, text: t.featIcal },
  ];

  const monthlyPrice = 19.90;
  const annualPrice = 199;
  const annualMonthly = annualPrice / 12; // numeric for fmtEur
  const savingsPercent = Math.round((1 - annualPrice / (monthlyPrice * 12)) * 100);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" style={body}>
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/3 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-indigo-500/10 rounded-full blur-3xl"></div>
      </div>

      <nav className="border-b border-white/10 bg-white/5 backdrop-blur-sm relative z-10">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/landing" className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-[22%] overflow-hidden flex-shrink-0"><img src={LOGO_URL} alt="Nautiqo" className="w-full h-full scale-[1.06]" /></div>
            <span className="font-bold text-xl text-white tracking-tight" style={heading}>Nautiqo</span>
          </Link>
          <div className="flex items-center gap-2">
            {token ? (
              <Link to="/admin" className="text-slate-400 hover:text-white text-sm font-medium transition">{t.navDashboard}</Link>
            ) : (
              <Link to="/login" className="text-slate-400 hover:text-white text-sm font-medium transition">{t.navLogin}</Link>
            )}
            <LanguageSwitcher variant="light" />
          </div>
        </div>
      </nav>

      <div className="relative z-10 max-w-5xl mx-auto px-4 py-16 sm:py-24">
        <div className="text-center mb-12">
          <span className="text-blue-400 text-sm font-semibold uppercase tracking-wider block mb-3">{t.subscriptionLabel}</span>
          <h1 className="text-3xl sm:text-5xl font-bold text-white tracking-tight mb-4" style={heading}>
            {t.heroTitle}
          </h1>
          <p className="text-lg text-slate-400 max-w-xl mx-auto">
            {t.heroSubtitle}
          </p>
        </div>

        {/* Billing toggle — pill switch with sliding thumb */}
        <div className="flex items-center justify-center mb-10" data-testid="billing-toggle">
          <div className="relative inline-flex items-center bg-white/8 backdrop-blur-md border border-white/10 rounded-full p-1 shadow-lg shadow-blue-900/20">
            {/* Sliding thumb */}
            <span
              aria-hidden="true"
              className="absolute top-1 bottom-1 rounded-full bg-white shadow-md transition-all duration-300 ease-out"
              style={{
                left: billing === 'monthly' ? '4px' : 'calc(50% + 0px)',
                width: 'calc(50% - 4px)',
              }}
            />
            <button
              onClick={() => setBilling('monthly')}
              className={`relative z-10 px-6 py-2.5 rounded-full text-sm font-semibold transition-colors duration-200 ${billing === 'monthly' ? 'text-slate-900' : 'text-slate-400 hover:text-slate-200'}`}
              data-testid="billing-monthly"
            >
              {t.monthly}
            </button>
            <button
              onClick={() => setBilling('annual')}
              className={`relative z-10 px-6 py-2.5 rounded-full text-sm font-semibold transition-colors duration-200 flex items-center gap-2 ${billing === 'annual' ? 'text-slate-900' : 'text-slate-400 hover:text-slate-200'}`}
              data-testid="billing-annual"
            >
              {t.annual}
              <span className={`text-xs font-bold px-2 py-0.5 rounded-full transition-colors ${billing === 'annual' ? 'bg-emerald-500/20 text-emerald-700' : 'bg-emerald-500/20 text-emerald-400'}`}>-{savingsPercent}%</span>
            </button>
          </div>
        </div>

        {/* Success banner from Stripe redirect */}
        {checkoutResult === 'success' && (
          <div className="max-w-lg mx-auto mb-6 flex items-center gap-3 bg-emerald-500/10 border border-emerald-500/30 rounded-xl px-5 py-4 text-emerald-300">
            <CheckCircle className="w-5 h-5 flex-shrink-0" />
            <div>
              <p className="font-semibold">{t.subActivatedTitle}</p>
              <p className="text-sm text-emerald-400/80">{t.subActivatedDesc}</p>
            </div>
            <Link to="/admin" className="ml-auto text-sm font-semibold text-white hover:text-emerald-300 transition flex-shrink-0">{t.navDashboard} →</Link>
          </div>
        )}
        {checkoutResult === 'cancelled' && (
          <div className="max-w-lg mx-auto mb-6 flex items-center gap-3 bg-slate-700/50 border border-white/10 rounded-xl px-5 py-4 text-slate-400">
            <p className="text-sm">{t.checkoutCancelled}</p>
          </div>
        )}

        {/* Pricing card */}
        <div className="max-w-lg mx-auto">
          <div className="bg-white/10 backdrop-blur-sm rounded-3xl border border-white/15 p-8 sm:p-10" data-testid="pricing-card">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-white mb-1" style={heading}>Nautiqo</h2>
              <p className="text-slate-500 text-sm mb-4 transition-opacity duration-200" key={`${billing}-label`}>{billing === 'annual' ? t.billingAnnual : t.billingMonthly}</p>

              <div className="relative h-[88px]" key={`${billing}-price`} style={{ animation: 'fadeInPrice 250ms cubic-bezier(0.16, 1, 0.3, 1)' }}>
                {billing === 'monthly' ? (
                  <>
                    <div className="flex items-baseline justify-center gap-1">
                      <span className="text-5xl font-bold text-white tracking-tight tabular-nums" style={heading}>€19,90</span>
                      <span className="text-slate-400 text-lg">{t.perMonth}</span>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex items-baseline justify-center gap-1">
                      <span className="text-5xl font-bold text-white tracking-tight tabular-nums" style={heading}>€199</span>
                      <span className="text-slate-400 text-lg">{t.perYear}</span>
                    </div>
                    <p className="text-emerald-400 text-sm font-medium mt-1">
                      {fmtEur(annualMonthly)}{t.perMonth} — {t.savePrefix} {fmtEur((monthlyPrice * 12) - annualPrice, false)} {t.savePerYearSuffix}
                    </p>
                  </>
                )}
              </div>

              <p className="text-slate-500 text-xs mt-3">{t.cancelAnytime}</p>
            </div>

            <div className="space-y-3 mb-8">
              {features.map((f, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-blue-500/15 rounded-lg flex items-center justify-center flex-shrink-0">
                    <f.icon className="w-4 h-4 text-blue-400" />
                  </div>
                  <span className="text-slate-300 text-sm">{f.text}</span>
                </div>
              ))}
            </div>

            {error && (
              <div className="bg-red-500/10 border border-red-500/20 text-red-400 text-sm rounded-xl px-4 py-3 mb-4">
                {error}
              </div>
            )}

            {/* Primary CTA: Stripe web checkout */}
            <button
              onClick={handleCheckout}
              disabled={loading}
              className="w-full py-4 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-bold rounded-xl transition flex items-center justify-center gap-2 text-base mb-4"
              data-testid="cta-stripe-checkout"
            >
              {loading ? (
                <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />{t.redirectingStripe}</>
              ) : (
                <><CreditCard className="w-5 h-5" />{t.subscribeWithCard}{token ? '' : t.registerFirst}</>
              )}
            </button>

            <div className="flex items-center gap-3 mb-4">
              <div className="flex-1 h-px bg-white/10" />
              <span className="text-xs text-slate-500">{t.orFromApp}</span>
              <div className="flex-1 h-px bg-white/10" />
            </div>

            <div className="grid grid-cols-2 gap-3" data-testid="store-cta-buttons">
              <a
                href="https://apps.apple.com/app/id6762418993"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full py-3 bg-white/8 hover:bg-white/12 border border-white/10 text-white font-medium rounded-xl transition flex items-center justify-center gap-2 text-sm"
                data-testid="cta-app-store"
              >
                <Apple className="w-4 h-4" />
                <span>App Store</span>
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=com.nautiqo.app"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full py-3 bg-white/8 hover:bg-white/12 border border-white/10 text-white font-medium rounded-xl transition flex items-center justify-center gap-2 text-sm"
                data-testid="cta-play-store"
              >
                <Smartphone className="w-4 h-4" />
                <span>Google Play</span>
              </a>
            </div>

            <p className="text-center text-xs text-slate-500 mt-4">
              {token ? <Link to="/admin" className="text-blue-400 hover:underline">{t.goToDashboard}</Link> : <>{t.alreadyHaveAccount} <Link to="/login" className="text-blue-400 hover:underline">{t.navLogin}</Link></>}
            </p>
          </div>

          <div className="flex items-center justify-center gap-6 mt-8 text-xs text-slate-500">
            <span className="flex items-center gap-1.5"><Shield className="w-3.5 h-3.5" /> {t.securePayment}</span>
            <span className="flex items-center gap-1.5"><CreditCard className="w-3.5 h-3.5" /> {t.cardsAccepted}</span>
            <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> {t.cancelOneClick}</span>
          </div>

          <p className="text-[11px] text-slate-500 text-center mt-5 max-w-xl mx-auto leading-relaxed" data-testid="mobile-pricing-disclaimer">
            <span className="italic">
              {t.mobilePricingDisclaimer}
            </span>
          </p>
        </div>
      </div>
    </div>
  );
};


// ==================== ACCOUNT PAGE ====================
export const AccountPage = () => {
  const [user, setUser] = useState(null);
  const [sub, setSub] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mutating, setMutating] = useState(false);
  // Cancellation flow: 'reason' (mini-survey) -> 'confirm' (final modal) -> null
  const [cancelStep, setCancelStep] = useState(null); // 'reason' | 'confirm' | null
  const [cancelReason, setCancelReason] = useState(null);
  const [cancelNote, setCancelNote] = useState("");
  const [showReactivate, setShowReactivate] = useState(false);
  const [showChangePlan, setShowChangePlan] = useState(false);
  const token = localStorage.getItem('nautiqo_token');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const authHeaders = { Authorization: `Bearer ${token}` };
  const t = usePageT(translations);
  usePageMeta({ title: t.metaAccountTitle, description: t.metaAccountDesc });

  const refresh = async () => {
    const userRes = await axios.get(`${API}/api/auth/me`, { headers: authHeaders });
    setUser(userRes.data);
    try {
      const subRes = await axios.get(`${API}/api/subscription/me`, { headers: authHeaders });
      setSub(subRes.data);
    } catch { setSub(null); }
    try {
      const txnRes = await axios.get(`${API}/api/subscription/transactions/${userRes.data.company_id || userRes.data.id}`);
      setTransactions(txnRes.data || []);
    } catch {}
  };

  useEffect(() => {
    if (!token) { navigate('/login'); return; }
    const checkoutResult = searchParams.get('checkout');
    if (checkoutResult === 'success') {
      toast.success(t.toastSubActivatedTitle, { description: t.toastSubActivatedDesc, duration: 6000 });
      setSearchParams({}, { replace: true });
    } else if (checkoutResult === 'cancelled') {
      toast.info(t.toastCheckoutCancelledTitle, { description: t.toastCheckoutCancelledDesc, duration: 4000 });
      setSearchParams({}, { replace: true });
    }
    refresh().catch(() => navigate('/login')).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCancel = async () => {
    setMutating(true);
    try {
      // 1) Save the reason (best-effort, non-blocking)
      if (cancelReason) {
        try {
          await axios.post(`${API}/api/subscription/cancel-feedback`,
            { reason: cancelReason, note: cancelNote },
            { headers: authHeaders });
        } catch {}
      }
      // 2) Trigger Stripe cancel_at_period_end
      await axios.post(`${API}/api/subscription/cancel`, {}, { headers: authHeaders });
      await refresh();
      toast.success(t.toastAutoRenewCancelledTitle, {
        description: `${t.toastAutoRenewCancelledDesc1} ${fmtDate(sub?.current_period_end || user?.subscription_end_date)}.`,
        duration: 6000,
      });
    } catch (err) {
      toast.error(t.toastCancelErrorTitle, {
        description: err.response?.data?.detail || t.toastTryLater,
      });
    } finally {
      setMutating(false);
      setCancelStep(null);
      setCancelReason(null);
      setCancelNote("");
    }
  };

  const handleReactivate = async () => {
    if (isAppleUser || isGoogleUser) {
      toast.info(t.toastReactivateAppTitle, {
        description: t.toastReactivateAppDesc,
        duration: 7000,
      });
      setShowReactivate(false);
      return;
    }
    // Stripe: open billing portal (handles reactivation of cancel_at_period_end)
    setShowReactivate(false);
    await handleOpenPortal();
  };

  const handleChangePlan = async (newInterval) => {
    if (isAppleUser || isGoogleUser) {
      toast.info(t.toastChangePlanAppTitle, {
        description: t.toastChangePlanAppDesc,
        duration: 7000,
      });
      setShowChangePlan(false);
      return;
    }
    // Stripe: call /change-plan
    setMutating(true);
    try {
      await axios.post(`${API}/api/change-plan`,
        { plan: 'pro', billing_cycle: newInterval },
        { headers: authHeaders });
      await refresh();
      toast.success(t.toastPlanUpdatedTitle, {
        description: newInterval === 'yearly' ? t.toastSwitchedAnnual : t.toastSwitchedMonthly,
        duration: 5000,
      });
    } catch (err) {
      toast.error(t.toastChangePlanErrorTitle, { description: err.response?.data?.detail || t.toastTryLater });
    } finally {
      setMutating(false);
      setShowChangePlan(false);
    }
  };

  const handleOpenPortal = async () => {
    setMutating(true);
    try {
      const res = await axios.post(`${API}/api/subscription/portal`,
        { return_url: window.location.href },
        { headers: authHeaders });
      window.location.href = res.data.url;
    } catch (err) {
      toast.error(t.toastPortalErrorTitle, {
        description: err.response?.data?.detail || t.toastTryLater,
      });
      setMutating(false);
    }
  };

  if (loading) return (
    <div className="p-4 lg:p-6 max-w-4xl mx-auto" data-testid="account-loading">
      <div className="h-7 w-32 bg-white/5 rounded mb-2 animate-pulse" />
      <div className="h-4 w-64 bg-white/5 rounded mb-8 animate-pulse" />
      <SkeletonHealthCard />
      <SkeletonAccountCard />
      <div className="bg-white/10 backdrop-blur-sm rounded-2xl border border-white/10 p-6">
        <div className="h-5 w-44 bg-white/5 rounded mb-4 animate-pulse" />
        <SkeletonList rows={3} />
      </div>
    </div>
  );

  const statusMap = {
    trialing: { label: t.statusTrialActive, color: 'bg-amber-500/15 text-amber-400' },
    trial: { label: t.statusTrialActive, color: 'bg-amber-500/15 text-amber-400' },
    active: { label: t.statusActive, color: 'bg-emerald-500/15 text-emerald-400' },
    cancelled: { label: t.statusRenewCancelled, color: 'bg-amber-500/15 text-amber-400' },
    past_due: { label: t.statusPastDue, color: 'bg-red-500/15 text-red-400' },
    expired: { label: t.statusExpired, color: 'bg-red-500/15 text-red-400' },
    trial_expired: { label: t.statusTrialExpired, color: 'bg-red-500/15 text-red-400' },
    inactive: { label: t.statusInactive, color: 'bg-slate-600/30 text-slate-400' },
  };

  // Decide visible status: prefer local subscription state (cancel pending) over external
  const externalStatus = user?.subscription_status;
  const isTrialing = ['trialing', 'trial'].includes(externalStatus);
  const cancelPending = sub?.has_subscription && sub?.cancel_at_period_end;
  const hasStripeSub = sub?.has_subscription && !sub?.is_legacy;
  const isLegacy = sub?.is_legacy;

  // Billing provider (new multi-provider field from /auth/me).
  // "stripe" | "apple" | "google" | null (manual/legacy)
  // For users with a local Stripe sub but billing_provider unset (legacy), we coerce to 'stripe'.
  const provider = user?.billing_provider || (hasStripeSub ? 'stripe' : null);
  const isAppleUser = provider === 'apple';
  const isGoogleUser = provider === 'google';
  const isStripeUser = provider === 'stripe';
  // Apple/Google subs can only be cancelled from the OS settings. We must NOT
  // call /subscription/cancel for these — it would do nothing anyway (no
  // stripe_subscription_id) but we want a clear UX.

  // visible label
  let displayStatus = externalStatus;
  if (cancelPending) displayStatus = 'cancelled';
  const visible = statusMap[displayStatus] || statusMap.inactive;

  // renewal / expiry date: prefer Stripe's current_period_end, fall back to external
  const renewalDate = sub?.current_period_end || user?.subscription_end_date;
  const daysLeft = renewalDate ? Math.max(0, Math.ceil((new Date(renewalDate) - new Date()) / (1000 * 60 * 60 * 24))) : 0;
  const isExpired = ['expired', 'trial_expired', 'inactive'].includes(externalStatus) && !cancelPending;

  // Internal interval code ('annual' | 'monthly' | null) — translated to label at render time
  const billingInterval = sub?.billing_interval === 'annual' ? 'annual'
    : sub?.billing_interval === 'monthly' ? 'monthly'
    : (transactions.find(tx => tx.payment_status === 'paid')?.billing_interval === 'annual' ? 'annual'
    : transactions.find(tx => tx.payment_status === 'paid')?.billing_interval === 'monthly' ? 'monthly' : null);
  const billingLabel = billingInterval === 'annual' ? t.annual : billingInterval === 'monthly' ? t.monthly : null;

  return (
    <div className="p-4 lg:p-6 max-w-4xl mx-auto" data-testid="account-page">
      <h1 className="text-2xl font-bold text-white mb-1">{t.accountTitle}</h1>
      <p className="text-slate-400 text-sm mb-8">{t.accountSubtitle}</p>

      {/* Health Score */}
      <HealthScoreCard token={token} />

      <div className="bg-white/10 backdrop-blur-sm rounded-2xl border border-white/10 p-6 mb-6" data-testid="subscription-card">
        <div className="flex items-start justify-between mb-5">
          <div>
            <h2 className="text-lg font-semibold text-white">{t.subscription}</h2>
            <span className={`text-xs font-semibold px-2.5 py-1 rounded-full mt-1 inline-block ${visible.color}`} data-testid="sub-status-badge">{visible.label}</span>
          </div>
          {(externalStatus === 'active' || cancelPending) && billingLabel && (
            <div className="text-right">
              <p className="text-2xl font-bold text-white tabular-nums" style={heading}>
                {billingInterval === 'annual' ? '€199' : '€19,90'}<span className="text-sm text-slate-400 font-normal">{billingInterval === 'annual' ? t.perYear : t.perMonth}</span>
              </p>
              <p className="text-xs text-slate-500">{billingLabel}</p>
            </div>
          )}
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
          <div>
            <p className="text-xs text-slate-500 mb-1">{t.plan}</p>
            <p className="text-sm font-medium text-white">{sub?.plan_name || user?.plan_name || 'Nautiqo'}</p>
          </div>
          <div>
            <p className="text-xs text-slate-500 mb-1">
              {isTrialing ? t.trialExpiresOn : cancelPending ? t.accessValidUntil : externalStatus === 'active' ? t.nextRenewal : t.expiredOn}
            </p>
            <p className="text-sm font-medium text-white" data-testid="sub-renewal-date">
              {renewalDate ? fmtDate(renewalDate) : '—'}
              {isTrialing && daysLeft > 0 && <span className="text-amber-400 ml-1">({daysLeft} {t.daysSuffix})</span>}
            </p>
          </div>
          <div>
            <p className="text-xs text-slate-500 mb-1">{t.channel}</p>
            <ProviderBadge provider={provider} />
          </div>
          <div>
            <p className="text-xs text-slate-500 mb-1">{t.email}</p>
            <p className="text-sm font-medium text-white truncate">{user?.email}</p>
          </div>
        </div>

        {/* CANCEL pending info */}
        {cancelPending && (
          <div className="bg-amber-500/5 border border-amber-500/15 rounded-xl p-4 mb-5 text-sm text-amber-200" data-testid="cancel-pending-info">
            <div className="flex items-start gap-2">
              <Clock className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-300" />
              <div>
                {t.cancelPendingPart1} <span className="font-semibold">{fmtDate(sub?.cancelled_at)}</span>{t.cancelPendingPart2} <span className="font-semibold">{fmtDate(renewalDate)}</span>{t.cancelPendingPart3}
              </div>
            </div>
          </div>
        )}

        {/* Trial info banner */}
        {isTrialing && daysLeft > 0 && (
          <div className="bg-blue-500/5 border border-blue-500/15 rounded-xl p-4 mb-5 text-sm text-blue-200" data-testid="trial-info">
            <div className="flex items-start gap-2">
              <Zap className="w-4 h-4 mt-0.5 flex-shrink-0 text-blue-300" />
              <div>
                {t.trialUsingPart1} <span className="font-semibold">{t.trialFreeTrial}</span>{t.trialUsingPart2} <span className="font-semibold">{daysLeft} {t.daysSuffix}</span> {t.trialUsingPart3}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ───── GESTIONE ABBONAMENTO — separate section, always actionable ───── */}
      <div className="bg-white/10 backdrop-blur-sm rounded-2xl border border-white/10 p-6 mb-6" data-testid="subscription-management">
        <h2 className="text-lg font-semibold text-white mb-1">Gestione abbonamento</h2>
        <p className="text-sm text-slate-400 mb-5">
          {isAppleUser && "Il tuo abbonamento è stato attivato tramite App Store e può essere gestito solo dal tuo iPhone/iPad."}
          {isGoogleUser && "Il tuo abbonamento è stato attivato tramite Google Play e può essere gestito solo dal tuo dispositivo Android."}
          {!isAppleUser && !isGoogleUser && hasStripeSub && !cancelPending && "Annulla il rinnovo automatico — l'accesso resta attivo fino alla scadenza."}
          {!isAppleUser && !isGoogleUser && hasStripeSub && cancelPending && "Riattiva il rinnovo automatico per continuare senza interruzioni."}
          {!isAppleUser && !isGoogleUser && !hasStripeSub && isExpired && "Il tuo abbonamento è scaduto. Riattivalo per tornare operativo."}
          {!isAppleUser && !isGoogleUser && !hasStripeSub && !isExpired && !isLegacy && "Configura il rinnovo automatico per non perdere mai l'accesso al servizio."}
          {!isAppleUser && !isGoogleUser && !hasStripeSub && !isExpired && isLegacy && "Hai un abbonamento attivo dal flusso precedente."}
        </p>

        <div className="space-y-3">
          {/* Caso APPLE — gestione via Impostazioni iPhone (NO Stripe actions) */}
          {isAppleUser && (
            <div className="bg-slate-100/5 border border-slate-300/10 rounded-xl p-5" data-testid="apple-manage-block">
              <div className="flex items-start gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-slate-100/10 flex items-center justify-center flex-shrink-0">
                  <Apple className="w-5 h-5 text-slate-200" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-white mb-1">Gestisci su App Store</p>
                  <p className="text-xs text-slate-400 leading-relaxed">Per modificare o annullare questo abbonamento:</p>
                </div>
              </div>
              <ol className="text-xs text-slate-400 space-y-2 pl-10 mb-4 list-decimal list-outside">
                <li>Apri <span className="text-white font-medium">Impostazioni</span> sul tuo iPhone o iPad</li>
                <li>Tocca <span className="text-white font-medium">il tuo nome</span> in cima</li>
                <li>Vai su <span className="text-white font-medium">Abbonamenti</span></li>
                <li>Seleziona <span className="text-white font-medium">Nautiqo</span> e scegli l'azione desiderata</li>
              </ol>
              <a
                href="https://apps.apple.com/account/subscriptions"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full flex items-center justify-center gap-2 py-3 bg-white/10 hover:bg-white/15 border border-white/10 text-white font-medium rounded-xl transition text-sm"
                data-testid="apple-manage-btn"
              >
                Apri Abbonamenti App Store
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>
          )}

          {/* Caso GOOGLE — gestione via Play Store (NO Stripe actions) */}
          {isGoogleUser && (
            <div className="bg-emerald-500/5 border border-emerald-500/15 rounded-xl p-5" data-testid="google-manage-block">
              <div className="flex items-start gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-emerald-500/15 flex items-center justify-center flex-shrink-0">
                  <Smartphone className="w-5 h-5 text-emerald-300" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-white mb-1">Gestisci su Google Play</p>
                  <p className="text-xs text-slate-400 leading-relaxed">Per modificare o annullare questo abbonamento:</p>
                </div>
              </div>
              <ol className="text-xs text-slate-400 space-y-2 pl-10 mb-4 list-decimal list-outside">
                <li>Apri l'app <span className="text-white font-medium">Play Store</span> su Android</li>
                <li>Tocca <span className="text-white font-medium">il tuo profilo</span> in alto a destra</li>
                <li>Vai su <span className="text-white font-medium">Pagamenti e abbonamenti → Abbonamenti</span></li>
                <li>Seleziona <span className="text-white font-medium">Nautiqo</span> e scegli l'azione desiderata</li>
              </ol>
              <a
                href={`https://play.google.com/store/account/subscriptions?package=com.nautiqo.app&sku=${user?.google_product_id || 'nautiqo'}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full flex items-center justify-center gap-2 py-3 bg-white/10 hover:bg-white/15 border border-white/10 text-white font-medium rounded-xl transition text-sm"
                data-testid="google-manage-btn"
              >
                Apri Abbonamenti Google Play
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>
          )}

          {/* Caso A: Stripe sub attiva, NO cancel pending — può cambiare piano + portal + annullare */}
          {!isAppleUser && !isGoogleUser && hasStripeSub && !cancelPending && (
            <>
              {/* Change plan + Portal — primary helpers */}
              <div className="grid sm:grid-cols-2 gap-3">
                <button
                  onClick={() => setShowChangePlan(true)}
                  disabled={mutating}
                  className="py-3 bg-white/8 hover:bg-white/12 border border-white/10 text-white font-medium rounded-xl transition disabled:opacity-50 flex items-center justify-center gap-2 text-sm"
                  data-testid="change-plan-btn"
                >
                  Cambia a {sub?.billing_interval === 'monthly' ? 'Annuale' : 'Mensile'}
                </button>
                <button
                  onClick={handleOpenPortal}
                  disabled={mutating}
                  className="py-3 bg-white/8 hover:bg-white/12 border border-white/10 text-white font-medium rounded-xl transition disabled:opacity-50 flex items-center justify-center gap-2 text-sm"
                  data-testid="portal-btn"
                >
                  <CreditCard className="w-4 h-4" /> Aggiorna pagamento e fatture
                </button>
              </div>

              <button
                onClick={() => setCancelStep('reason')}
                disabled={mutating}
                className="w-full py-3 border border-red-500/30 text-red-300 hover:bg-red-500/10 font-semibold rounded-xl transition disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="cancel-sub-btn"
              >
                Annulla rinnovo automatico
              </button>
              <p className="text-xs text-slate-500 text-center">
                Il tuo account resta attivo fino al <span className="font-medium text-slate-300">{fmtDate(renewalDate)}</span>. Nessun addebito al rinnovo.
              </p>
            </>
          )}

          {/* Caso B: Stripe sub con cancel pending — può riattivare + portal */}
          {!isAppleUser && !isGoogleUser && hasStripeSub && cancelPending && (
            <>
              <button
                onClick={() => setShowReactivate(true)}
                disabled={mutating}
                className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl transition disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="reactivate-sub-btn"
              >
                Riattiva rinnovo automatico
                <ArrowRight className="w-4 h-4" />
              </button>
              <button
                onClick={handleOpenPortal}
                disabled={mutating}
                className="w-full py-3 bg-white/8 hover:bg-white/12 border border-white/10 text-white font-medium rounded-xl transition disabled:opacity-50 flex items-center justify-center gap-2 text-sm"
                data-testid="portal-btn"
              >
                <CreditCard className="w-4 h-4" /> Aggiorna pagamento e fatture
              </button>
              <p className="text-xs text-slate-500 text-center">
                Verrà addebitato {billingLabel === 'Annuale' ? fmtEur(199, false) : fmtEur(19.90)} il <span className="font-medium text-slate-300">{fmtDate(renewalDate)}</span>.
              </p>
            </>
          )}

          {/* Caso C: scaduto → vai a /pricing */}
          {!isAppleUser && !isGoogleUser && !hasStripeSub && isExpired && (
            <Link
              to="/pricing"
              className="w-full flex items-center justify-center gap-2 py-3 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 transition"
              data-testid="activate-btn"
            >
              Riattiva abbonamento
              <ArrowRight className="w-4 h-4" />
            </Link>
          )}

          {/* Caso D: trial/active senza Stripe sub (mai checkout fatto) → configura */}
          {!isAppleUser && !isGoogleUser && !hasStripeSub && !isExpired && !isLegacy && (
            <>
              <Link
                to="/pricing"
                className="w-full flex items-center justify-center gap-2 py-3 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 transition"
                data-testid="setup-renewal-btn"
              >
                Configura rinnovo automatico
                <ArrowRight className="w-4 h-4" />
              </Link>
              <p className="text-xs text-slate-500 text-center">
                Scegli Mensile o Annuale. Cancella quando vuoi, nessun vincolo.
              </p>
            </>
          )}

          {/* Caso E: legacy (one-shot precedente) — non si può annullare via API */}
          {!isAppleUser && !isGoogleUser && !hasStripeSub && !isExpired && isLegacy && (
            <>
              <a
                href="mailto:info@nautiqo.cloud?subject=Modifica%20abbonamento"
                className="w-full flex items-center justify-center gap-2 py-3 border border-white/15 text-slate-200 hover:bg-white/5 font-semibold rounded-xl transition"
                data-testid="legacy-contact-btn"
              >
                Contatta l'assistenza
              </a>
              <Link
                to="/pricing"
                className="w-full flex items-center justify-center gap-2 py-3 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 transition text-sm"
                data-testid="legacy-upgrade-btn"
              >
                Passa al nuovo piano con rinnovo automatico
                <ArrowRight className="w-4 h-4" />
              </Link>
              <p className="text-xs text-slate-500 text-center" data-testid="legacy-info">
                Il tuo piano attuale non supporta annullamento automatico via web. Scrivici per modifiche o passa al nuovo piano.
              </p>
            </>
          )}
        </div>
      </div>

      <div className="bg-white/10 backdrop-blur-sm rounded-2xl border border-white/10 p-6 mb-6">
        <h2 className="text-lg font-semibold text-white mb-4">Informazioni account</h2>
        <div className="grid sm:grid-cols-2 gap-4 text-sm">
          <div><p className="text-slate-500">Azienda</p><p className="text-white font-medium">{user?.company_name || '-'}</p></div>
          <div><p className="text-slate-500">Email</p><p className="text-white font-medium">{user?.email || '-'}</p></div>
          <div><p className="text-slate-500">Telefono</p><p className="text-white font-medium">{user?.phone || '-'}</p></div>
          <div><p className="text-slate-500">Registrato il</p><p className="text-white font-medium">{user?.created_at ? fmtDate(user.created_at) : '-'}</p></div>
        </div>
      </div>

      {transactions.length > 0 && (
        <div className="bg-white/10 backdrop-blur-sm rounded-2xl border border-white/10 p-6">
          <h2 className="text-lg font-semibold text-white mb-4">Storico pagamenti</h2>
          <div className="space-y-3">
            {transactions.map((t, i) => (
              <div key={i} className="flex items-center justify-between py-3 border-b border-white/10 last:border-0">
                <div>
                  <p className="text-sm font-medium text-white">{t.plan || 'Nautiqo'}</p>
                  <p className="text-xs text-slate-500">
                    {t.created_at ? fmtDate(t.created_at) : '-'}
                    {t.billing_interval && <span className="ml-1">· {t.billing_interval === 'annual' ? 'Annuale' : 'Mensile'}</span>}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium text-white tabular-nums">{fmtEur(t.amount)}</p>
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                    t.payment_status === 'paid' ? 'bg-emerald-500/15 text-emerald-400' :
                    t.payment_status === 'initiated' ? 'bg-amber-500/15 text-amber-400' :
                    'bg-slate-600/30 text-slate-400'
                  }`}>{t.payment_status === 'paid' ? 'Pagato' : t.payment_status === 'initiated' ? 'In attesa' : t.payment_status}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ───── Modals ───── */}

      {/* Cancel — Step 1: Mini-survey (reason) */}
      {cancelStep === 'reason' && (
        <Modal onClose={() => { setCancelStep(null); setCancelReason(null); }} testId="cancel-reason-modal">
          <h3 className="text-lg font-semibold text-white mb-1">Prima di annullare…</h3>
          <p className="text-sm text-slate-400 mb-5">Aiutaci a migliorare. Cosa ti porta ad annullare?</p>

          <div className="space-y-2 mb-4">
            {[
              { key: 'price', label: 'Costo troppo alto', hint: 'Possiamo proporti uno sconto?' },
              { key: 'features', label: 'Mancano funzionalità che mi servono', hint: 'Dimmi quali nello spazio sotto' },
              { key: 'low_usage', label: 'Lo uso poco / stagione bassa', hint: 'Possiamo metterlo in pausa per qualche mese' },
              { key: 'alternative', label: 'Ho trovato un\'alternativa', hint: '' },
              { key: 'other', label: 'Altro motivo', hint: '' },
            ].map(opt => (
              <label
                key={opt.key}
                className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition ${
                  cancelReason === opt.key ? 'border-blue-400/50 bg-blue-500/10' : 'border-white/10 hover:border-white/20 hover:bg-white/5'
                }`}
                data-testid={`cancel-reason-${opt.key}`}
              >
                <input
                  type="radio"
                  name="cancel-reason"
                  value={opt.key}
                  checked={cancelReason === opt.key}
                  onChange={() => setCancelReason(opt.key)}
                  className="mt-0.5 accent-blue-500"
                />
                <div className="flex-1">
                  <p className="text-sm font-medium text-white">{opt.label}</p>
                  {cancelReason === opt.key && opt.hint && <p className="text-xs text-blue-300 mt-0.5">{opt.hint}</p>}
                </div>
              </label>
            ))}
          </div>

          {cancelReason && (cancelReason === 'features' || cancelReason === 'other') && (
            <textarea
              value={cancelNote}
              onChange={(e) => setCancelNote(e.target.value)}
              placeholder={cancelReason === 'features' ? t.notePlaceholderFeatures : t.notePlaceholderOther}
              maxLength={500}
              rows={3}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm text-white placeholder:text-slate-500 outline-none focus:border-blue-400/50 mb-4 resize-none"
              data-testid="cancel-note"
            />
          )}

          <div className="flex gap-3">
            <button
              onClick={() => { setCancelStep(null); setCancelReason(null); setCancelNote(""); }}
              disabled={mutating}
              className="flex-1 py-2.5 border border-white/10 text-slate-300 rounded-xl hover:bg-white/5 transition disabled:opacity-50"
              data-testid="cancel-survey-back"
            >
              Indietro
            </button>
            <button
              onClick={() => setCancelStep('confirm')}
              disabled={!cancelReason || mutating}
              className="flex-1 py-2.5 bg-red-600 hover:bg-red-700 disabled:bg-red-600/40 text-white font-semibold rounded-xl transition"
              data-testid="cancel-survey-next"
            >
              Continua
            </button>
          </div>
        </Modal>
      )}

      {/* Cancel — Step 2: Final confirmation */}
      {cancelStep === 'confirm' && (
        <Modal onClose={() => setCancelStep('reason')} testId="cancel-confirm-modal">
          <h3 className="text-lg font-semibold text-white mb-2">Annullare il rinnovo automatico?</h3>
          <p className="text-sm text-slate-400 mb-5">
            Il tuo account resterà attivo fino al <span className="text-white font-medium">{fmtDate(renewalDate)}</span>. Non verrà addebitato alcun importo al rinnovo. Puoi riattivarlo in qualsiasi momento prima della scadenza.
          </p>
          <div className="flex gap-3">
            <button
              onClick={() => setCancelStep('reason')}
              disabled={mutating}
              className="flex-1 py-2.5 border border-white/10 text-slate-300 rounded-xl hover:bg-white/5 transition disabled:opacity-50"
              data-testid="cancel-confirm-back"
            >
              Indietro
            </button>
            <button
              onClick={handleCancel}
              disabled={mutating}
              className="flex-1 py-2.5 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-xl transition disabled:opacity-50"
              data-testid="cancel-confirm-submit"
            >
              {mutating ? 'Attendere…' : 'Conferma annullamento'}
            </button>
          </div>
        </Modal>
      )}

      {/* Reactivate confirm */}
      {showReactivate && (
        <Modal onClose={() => setShowReactivate(false)} testId="reactivate-modal">
          <h3 className="text-lg font-semibold text-white mb-2">Riattivare il rinnovo automatico?</h3>
          <p className="text-sm text-slate-400 mb-5">
            L'abbonamento verrà rinnovato automaticamente il <span className="text-white font-medium">{fmtDate(renewalDate)}</span> con addebito di <span className="text-white font-medium">{billingLabel === 'Annuale' ? fmtEur(199, false) : fmtEur(19.90)}</span>.
          </p>
          <div className="flex gap-3">
            <button
              onClick={() => setShowReactivate(false)}
              disabled={mutating}
              className="flex-1 py-2.5 border border-white/10 text-slate-300 rounded-xl hover:bg-white/5 transition disabled:opacity-50"
            >
              Annulla
            </button>
            <button
              onClick={handleReactivate}
              disabled={mutating}
              className="flex-1 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl transition disabled:opacity-50"
              data-testid="reactivate-confirm"
            >
              {mutating ? 'Attendere…' : 'Conferma riattivazione'}
            </button>
          </div>
        </Modal>
      )}

      {/* Change plan */}
      {showChangePlan && (
        <Modal onClose={() => setShowChangePlan(false)} testId="change-plan-modal">
          <h3 className="text-lg font-semibold text-white mb-2">
            {sub?.billing_interval === 'monthly' ? t.switchToPlanQuestionAnnual : t.switchToPlanQuestionMonthly}
          </h3>
          <p className="text-sm text-slate-400 mb-2">
            {sub?.billing_interval === 'monthly'
              ? <>{t.changePlanAnnualPart1} <span className="text-white font-medium">{fmtEur(199, false)}{t.changePlanAnnualYear}</span>{t.changePlanAnnualPart2} <span className="text-emerald-400 font-medium">{fmtEur((19.90 * 12) - 199, false)}</span> {t.changePlanAnnualPart3}</>
              : <>{t.changePlanMonthlyPart1} <span className="text-white font-medium">{fmtEur(19.90)}{t.changePlanMonthlyMonth}</span>{t.changePlanMonthlyPart2}</>}
          </p>
          <p className="text-xs text-slate-500 mb-5">
            {t.prorationPart1}<span className="font-medium text-slate-400">{t.prorationWord}</span>{t.prorationPart2}
          </p>
          <div className="flex gap-3">
            <button
              onClick={() => setShowChangePlan(false)}
              disabled={mutating}
              className="flex-1 py-2.5 border border-white/10 text-slate-300 rounded-xl hover:bg-white/5 transition disabled:opacity-50"
            >
              Annulla
            </button>
            <button
              onClick={() => handleChangePlan(sub?.billing_interval === 'monthly' ? 'annual' : 'monthly')}
              disabled={mutating}
              className="flex-1 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl transition disabled:opacity-50"
              data-testid="change-plan-confirm"
            >
              {mutating ? 'Attendere…' : 'Conferma cambio'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
};

// Reusable modal wrapper
const Modal = ({ children, onClose, testId }) => (
  <div
    className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
    onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    data-testid={testId}
  >
    <div className="bg-slate-900 border border-white/10 rounded-2xl p-6 max-w-md w-full">
      {children}
    </div>
  </div>
);

// updated
// updated
// trigger
